/**
 * =============================================================================
 * QUESTION SKELETON COMPONENT
 * =============================================================================
 *
 * Displays a loading skeleton placeholder that mimics the structure of a
 * question item to prevent layout shift (CLS) during data loading.
 *
 * TASK REFERENCE: Phase 0C Task 0C-38 - Loading Skeletons & Optimistic UI
 *
 * Features:
 * - Matches exact dimensions of QuestionItem to eliminate layout shift
 * - Uses shimmer animation for visual feedback
 * - Accessible with aria-busy and aria-label
 * - High contrast mode support
 *
 * =============================================================================
 */

import React from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface QuestionSkeletonProps {
  /** Number of skeleton items to display (default: 3) */
  count?: number;
  /** Additional CSS class name */
  className?: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Single skeleton item that mimics a question card structure.
 * Matches the exact dimensions of .fmp-question-item to prevent CLS.
 */
function SkeletonItem(): React.ReactElement {
  return (
    <li className="fmp-question-item fmp-skeleton-item">
      <div className="fmp-question-item__header">
        {/* Title placeholder - 70% width to mimic typical title */}
        <div className="fmp-skeleton__title" />
        {/* Status badge placeholder */}
        <div className="fmp-skeleton__badge" />
      </div>
      {/* Every final Slice 2D card carries a current-stage explanation. */}
      <div className="fmp-skeleton__description fmp-skeleton__stage" />
      <div className="fmp-question-item__footer">
        <div className="fmp-question-item__meta">
          {/* Date placeholder */}
          <div className="fmp-skeleton__meta" />
          {/* Image count placeholder (sometimes present) */}
          <div className="fmp-skeleton__meta fmp-skeleton__meta--short" />
        </div>
      </div>
    </li>
  );
}

/**
 * Loading skeleton component for question list.
 *
 * Displays multiple skeleton items with staggered animations
 * to indicate loading state while maintaining layout stability.
 *
 * @example
 * ```tsx
 * // Show 3 skeleton items (default)
 * <QuestionSkeleton />
 *
 * // Show 5 skeleton items
 * <QuestionSkeleton count={5} />
 * ```
 */
export function QuestionSkeleton({
  count = 3,
  className = '',
}: QuestionSkeletonProps): React.ReactElement {
  return (
    <div
      className={`fmp-skeleton-container ${className}`}
      role="status"
      aria-busy="true"
      aria-label="Loading questions"
    >
      <ul className="fmp-questions__list" aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
          <SkeletonItem key={index} />
        ))}
      </ul>
      {/* Screen reader announcement */}
      <span className="fmp-visually-hidden">Loading your questions...</span>
    </div>
  );
}
